import { useState } from "react";
import { useTranslation } from "react-i18next";
import { normalizeAboutImages } from "@cliently/schemas";
import {
  User,
  GraduationCap,
  Globe,
  Instagram,
  Briefcase,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Images,
} from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Avatar,
  AvatarFallback,
  Separator,
  ProfilePhotoAvatar,
} from "@cliently/ui";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { api } from "../lib/api";
import { ALL_CLIENTS_KEY, useOrgStore } from "../stores/org.store";
import { resolveOrganizationMediaUrl } from "../lib/organization-media-url";
import { portalProfessionTranslationKey } from "@cliently/types";
import { BookingServicePresentationCard } from "../components/booking/BookingServicePresentationCard";
import type { PortalServiceItem } from "../types/portal-service-item";

interface Diploma {
  label: string;
  imageUrl?: string | null;
}



const useAboutServiceItems = (orgId: string | null, isAllProsMode: boolean) => {
  return useQuery({
    queryKey: ["about-service-items", isAllProsMode ? orgId : "single"],
    queryFn: async () => {
      if (isAllProsMode) {
        if (!orgId) return [] as PortalServiceItem[];
        const { data } = await api.get("/portal/aggregate/service-items", {
          params: { orgId },
        });
        return data as PortalServiceItem[];
      }
      const { data } = await api.get("/portal/service-items");
      return data as PortalServiceItem[];
    },
    enabled: isAllProsMode ? !!orgId : true,
    staleTime: 1000 * 60 * 10,
  });
}



const useAbout = () => {
  return useQuery({
    queryKey: ["portal-about"],
    queryFn: async () => {
      const { data } = await api.get("/portal/about");
      return data as {
        name: string;
        logo: string | null;
        profilePhoto: string | null;
        profilePhotoPosition: string | null;
        bio: string | null;
        aboutImages: Array<{ url: string; caption?: string | null }> | null;
        diplomas: Diploma[] | null;
        professionType: string | null;
        customProfession: string | null;
        website: string | null;
        instagram: string | null;
      };
    },
    retry: false,
  });
}



const useAggregateAbout = () => {
  return useQuery({
    queryKey: ["portal-about-aggregate"],
    queryFn: async () => {
      const { data } = await api.get("/portal/aggregate/about");
      return data as Array<{
        id: string;
        name: string;
        logo: string | null;
        profilePhoto: string | null;
        profilePhotoPosition: string | null;
        bio: string | null;
        aboutImages: Array<{ url: string; caption?: string | null }> | null;
        diplomas: Diploma[] | null;
        professionType: string | null;
        customProfession: string | null;
        website: string | null;
        instagram: string | null;
      }>;
    },
    retry: false,
  });
}



const ProfessionLabel = ({
  professionType,
  customProfession,
}: {
  professionType: string | null;
  customProfession: string | null;
}) => {
  const { t } = useTranslation();
  if (!professionType) return null;

  const label =
    professionType === "GENERIC"
      ? customProfession || t("portal.practitioner")
      : (() => {
          const key = portalProfessionTranslationKey(professionType);
          return key ? t(key) : professionType;
        })();

  return (
    <Badge variant="secondary" className="text-sm">
      <Briefcase className="mr-1.5 h-3.5 w-3.5" />
      {label}
    </Badge>
  );
}



export const AboutPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const activeClientId = useOrgStore((s) => s.activeClientId);
  const isAllProsMode = activeClientId === ALL_CLIENTS_KEY;
  const { data: about, isLoading } = useAbout();
  const { data: aggregateAbout, isLoading: isAggregateLoading } =
    useAggregateAbout();
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  const effectiveList = isAllProsMode
    ? (aggregateAbout ?? [])
    : about
      ? [about]
      : [];
  const safeIndex = Math.min(
    selectedIndex,
    Math.max(0, effectiveList.length - 1),
  );
  const current = effectiveList[safeIndex];
  const currentOrgId = isAllProsMode
    ? ((current as { id?: string } | undefined)?.id ?? null)
    : null;
  const { data: serviceItems = [], isLoading: isServicesLoading } =
    useAboutServiceItems(currentOrgId, isAllProsMode);
  const currentProfileImageUrl = resolveOrganizationMediaUrl(
    current?.profilePhoto ?? current?.logo,
  );

  if ((isAllProsMode && isAggregateLoading) || (!isAllProsMode && isLoading)) {
    return (
      <div className="space-y-5">
        <div className="bg-muted h-10 w-64 animate-pulse rounded-lg" />
        <div className="bg-muted h-52 animate-pulse rounded-2xl" />
        <div className="bg-muted h-36 animate-pulse rounded-2xl" />
        <div className="bg-muted h-28 animate-pulse rounded-2xl" />
      </div>
    );
  }

  if (!current) return null;

  const diplomas = current.diplomas ?? [];
  const aboutImages = normalizeAboutImages(current.aboutImages)
    .map(({ url, caption }) => {
      const src = resolveOrganizationMediaUrl(url);
      if (!src) return null;
      return { src, caption: caption?.trim() || null };
    })
    .filter((item): item is { src: string; caption: string | null } =>
      Boolean(item),
    );
  const hasProfile =
    current.bio ||
    current.profilePhoto ||
    diplomas.length > 0 ||
    aboutImages.length > 0;
  const hasLinks = current.website || current.instagram;

  return (
    <div className="space-y-5 pb-8 sm:space-y-6">
      {/* ---------- Header ---------- */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
          {t("about.title")}
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          {t("about.subtitle")}
        </p>
      </div>

      {/* ---------- Practitioner navigation ---------- */}
      {isAllProsMode && effectiveList.length > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="rounded-full shadow-sm"
            aria-label={t(
              "about.previousPractitioner",
              "Praticien précédent",
            )}
            onClick={() =>
              setSelectedIndex(
                (i) =>
                  (i - 1 + effectiveList.length) % effectiveList.length,
              )
            }
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="min-w-0 px-3 text-center">
            <p className="truncate text-sm font-semibold">{current.name}</p>
            <p className="text-muted-foreground text-xs">
              {safeIndex + 1} / {effectiveList.length}
            </p>
          </div>

          <Button
            type="button"
            variant="outline"
            size="icon"
            className="rounded-full shadow-sm"
            aria-label={t("about.nextPractitioner", "Praticien suivant")}
            onClick={() =>
              setSelectedIndex(
                (i) => (i + 1) % effectiveList.length,
              )
            }
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* ---------- Profile hero card ---------- */}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <div className="flex flex-col sm:flex-row">
            {/* Image */}
            <div className="bg-muted/60 flex aspect-square w-full items-center justify-center sm:aspect-auto sm:w-48 sm:shrink-0 sm:min-h-48 md:w-56">
              {currentProfileImageUrl ? (
                <ProfilePhotoAvatar
                  src={currentProfileImageUrl}
                  alt={current.name}
                  position={current.profilePhotoPosition}
                  className="h-28 w-28 sm:h-32 sm:w-32"
                />
              ) : (
                <Avatar className="h-28 w-28 border-none bg-transparent sm:h-24 sm:w-24">
                  <AvatarFallback className="bg-primary/10 text-primary text-4xl font-bold sm:text-3xl">
                    {current.name?.[0] ?? "?"}
                  </AvatarFallback>
                </Avatar>
              )}
            </div>

            {/* Info */}
            <div className="flex flex-1 flex-col justify-center gap-3 p-5 sm:p-6">
              <div className="space-y-2">
                <h2 className="text-xl font-bold sm:text-2xl">
                  {current.name}
                </h2>
                <ProfessionLabel
                  professionType={current.professionType}
                  customProfession={current.customProfession}
                />
              </div>

              {hasLinks && (
                <>
                  <Separator />
                  <div className="flex flex-wrap items-center gap-3">
                    {current.website && (
                      <a
                        href={
                          current.website.startsWith("http")
                            ? current.website
                            : `https://${current.website}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-primary inline-flex items-center gap-1.5 text-sm transition-colors"
                      >
                        <Globe className="h-4 w-4" />
                        {t("about.website")}
                        <ExternalLink className="h-3 w-3 opacity-50" />
                      </a>
                    )}
                    {current.instagram && (
                      <a
                        href={
                          current.instagram.startsWith("http")
                            ? current.instagram
                            : `https://instagram.com/${current.instagram.replace(/^@/, "")}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-primary inline-flex items-center gap-1.5 text-sm transition-colors"
                      >
                        <Instagram className="h-4 w-4" />
                        Instagram
                        <ExternalLink className="h-3 w-3 opacity-50" />
                      </a>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ---------- Bio ---------- */}
      {current.bio && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2.5">
              <div className="bg-primary/10 flex h-8 w-8 items-center justify-center rounded-lg">
                <User className="text-primary h-4 w-4" />
              </div>
              <CardTitle>{t("about.bio")}</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground whitespace-pre-wrap text-sm leading-relaxed">
              {current.bio}
            </p>
          </CardContent>
        </Card>
      )}

      {/* ---------- À propos en images ---------- */}
      {aboutImages.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2.5">
              <div className="bg-primary/10 flex h-8 w-8 items-center justify-center rounded-lg">
                <Images className="text-primary h-4 w-4" />
              </div>
              <CardTitle>{t("about.gallery", "En images")}</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {aboutImages.map((item, i) => (
                <figure key={item.src} className="flex flex-col">
                  <a
                    href={item.src}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-muted aspect-square overflow-hidden rounded-xl border transition-transform duration-200 hover:scale-[1.02]"
                    aria-label={
                      item.caption
                        ? `Image : ${item.caption}`
                        : `Image ${i + 1}`
                    }
                  >
                    <img
                      src={item.src}
                      alt={item.caption ?? ""}
                      className="h-full w-full object-cover"
                    />
                  </a>
                  {item.caption ? (
                    <figcaption className="text-muted-foreground mt-1.5 text-center text-xs leading-snug">
                      {item.caption}
                    </figcaption>
                  ) : null}
                </figure>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ---------- Diplomas ---------- */}
      {diplomas.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2.5">
              <div className="bg-primary/10 flex h-8 w-8 items-center justify-center rounded-lg">
                <GraduationCap className="text-primary h-4 w-4" />
              </div>
              <CardTitle>{t("about.diplomas")}</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2">
              {diplomas.map((d, i) => (
                <div
                  key={i}
                  className="bg-muted/40 flex items-center gap-3 rounded-xl p-3"
                >
                  {d.imageUrl ? (
                    <img
                      src={resolveOrganizationMediaUrl(d.imageUrl)}
                      alt={d.label}
                      className="h-11 w-11 shrink-0 rounded-lg border object-cover"
                    />
                  ) : (
                    <div className="bg-background flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border">
                      <GraduationCap className="text-muted-foreground h-5 w-5" />
                    </div>
                  )}
                  <span className="text-sm font-medium leading-snug">
                    {d.label}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ---------- Services / booking ---------- */}
      {!isServicesLoading && serviceItems.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2.5">
              <div className="bg-primary/10 flex h-8 w-8 items-center justify-center rounded-lg">
                <Calendar className="text-primary h-4 w-4" />
              </div>
              <CardTitle>{t("booking.title")}</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {serviceItems.map((service) => (
                <BookingServicePresentationCard
                  key={service.id}
                  service={service}
                  bookLabel={t("booking.title")}
                  onBook={() => {
                    const params = new URLSearchParams();
                    params.set("from", "about");
                    if (isAllProsMode && currentOrgId) {
                      params.set("orgId", currentOrgId);
                    }
                    params.set("serviceItemId", service.id);
                    navigate(`/appointments?${params.toString()}`);
                  }}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ---------- Empty state ---------- */}
      {!hasProfile && (
        <Card>
          <CardContent className="py-16 text-center">
            <div className="bg-muted mx-auto flex h-16 w-16 items-center justify-center rounded-full">
              <User className="text-muted-foreground h-7 w-7" />
            </div>
            <p className="text-muted-foreground mt-4 text-sm">
              {t("about.noProfile")}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
